import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaSpinner, FaTrophy, FaChartBar, FaGamepad, FaPaperPlane, FaShieldAlt } from 'react-icons/fa';
import { UserContext } from './App';

const BACKEND_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://test3-0ar4.onrender.com';

const UserStatsModal = () => {
  const { user, activeBalance } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Tipping state
  const [tipAmount, setTipAmount] = useState('');
  const [tipping, setTipping] = useState(false);
  const [tipStatus, setTipStatus] = useState(null);

  useEffect(() => {
    window.showUserStats = (user) => {
      setUsername(user);
      setIsOpen(true);
      setTipAmount('');
      setTipStatus(null);
      fetchStats(user);
    };
    
    return () => { delete window.showUserStats; };
  }, []);

  const handleTip = async () => {
    if (!tipAmount || parseFloat(tipAmount) <= 0) return;
    
    setTipping(true);
    setTipStatus(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/user/tip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipientUsername: username,
          amount: parseFloat(tipAmount),
          usePromoBalance: activeBalance === 'promo'
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        setTipStatus({ type: 'success', message: `Successfully tipped $${tipAmount}!` });
        setTipAmount('');
      } else {
        setTipStatus({ type: 'error', message: data.error || 'Failed to tip' });
      }
    } catch (e) {
      setTipStatus({ type: 'error', message: 'Server error' });
    } finally {
      setTipping(false);
    }
  };

  const fetchStats = async (user) => {
    setLoading(true);
    setStats(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/stats/${encodeURIComponent(user)}`);
      if(response.ok) {
        setStats(await response.json());
      }
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if(!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)', padding: '20px'
    }} onClick={() => setIsOpen(false)}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 10 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--border-radius, 12px)',
          width: '100%', maxWidth: '380px',
          boxShadow: 'var(--card-shadow, 0 10px 30px rgba(0,0,0,0.5))',
          overflow: 'hidden', display: 'flex', flexDirection: 'column'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header / Close button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}>
           <button 
             onClick={() => setIsOpen(false)}
             style={{
               background: 'rgba(255,255,255,0.05)', border: 'none', color: 'var(--text-muted)',
               width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
               cursor: 'pointer', transition: 'all 0.2s'
             }}
             onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'var(--danger, #ef4444)'; }}
             onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
           >
             <FaTimes />
           </button>
        </div>

        {loading ? (
           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0', minHeight: '300px' }}>
              <FaSpinner className="fa-spin" style={{ fontSize: '32px', color: 'var(--accent, #ffb703)' }} />
           </div>
        ) : stats ? (
          <div style={{ padding: '0 24px 24px' }}>
            {/* User Profile Header */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', marginTop: '-10px' }}>
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                background: 'var(--primary, #0e0e1a)',
                border: '3px solid var(--border, #1c1c32)',
                padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)', marginBottom: '16px'
              }}>
                <img src={stats.avatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: 'var(--accent-gradient, linear-gradient(135deg, #ffb703, #ffc130))',
                  color: '#000', fontSize: '11px', fontWeight: '800',
                  padding: '4px 8px', borderRadius: '6px', letterSpacing: '0.5px'
                }}>
                  LVL {stats.level?.current || stats.level || 1}
                </span>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text, #fff)' }}>
                  {stats.username}
                </h2>
              </div>
              
              {stats.roles?.includes('Streamer') && (
                <div style={{ marginTop: '8px' }}>
                  <span style={{
                    background: 'rgba(255, 183, 3, 0.1)', color: '#ffb703', border: '1px solid rgba(255, 183, 3, 0.3)',
                    fontSize: '10px', fontWeight: '800', padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase'
                  }}>
                    Streamer
                  </span>
                </div>
              )}
            </div>

            {/* Stats List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{
                background: 'var(--primary, #0e0e1a)', border: '1px solid var(--border, #1c1c32)',
                padding: '16px', borderRadius: 'var(--button-radius, 8px)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ color: 'var(--text-muted, #8a8aa5)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaTrophy style={{ color: 'var(--text-muted)' }} /> Highest Win
                </span>
                <span style={{ color: 'var(--text, #fff)', fontSize: '15px', fontWeight: '700' }}>
                  ${(stats.highestWin || 0).toFixed(2)}
                </span>
              </div>

              <div style={{
                background: 'var(--primary, #0e0e1a)', border: '1px solid var(--border, #1c1c32)',
                padding: '16px', borderRadius: 'var(--button-radius, 8px)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ color: 'var(--text-muted, #8a8aa5)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaGamepad style={{ color: 'var(--text-muted)' }} /> Games Played
                </span>
                <span style={{ color: 'var(--text, #fff)', fontSize: '15px', fontWeight: '700' }}>
                  {stats.gamesPlayed || 0}
                </span>
              </div>

              <div style={{
                background: 'var(--primary, #0e0e1a)', border: '1px solid var(--border, #1c1c32)',
                padding: '16px', borderRadius: 'var(--button-radius, 8px)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ color: 'var(--text-muted, #8a8aa5)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaChartBar style={{ color: 'var(--text-muted)' }} /> Total Wagered
                </span>
                <span style={{ color: 'var(--text, #fff)', fontSize: '15px', fontWeight: '700' }}>
                  ${(stats.totalWagered || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: 'var(--border, #1c1c32)', margin: '24px 0' }} />

            {/* Send a Tip */}
            <div>
              <div style={{ color: 'var(--text-muted, #8a8aa5)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <FaPaperPlane size={12} /> Send a Tip
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                  <input 
                    type="number" 
                    placeholder="Amount" 
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    style={{
                      width: '100%', background: 'var(--primary, #0e0e1a)', border: '1px solid var(--border, #1c1c32)',
                      padding: '12px 12px 12px 28px', borderRadius: 'var(--button-radius, 8px)', color: 'var(--text, #fff)',
                      fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--accent, #ffb703)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border, #1c1c32)'}
                  />
                </div>
                <button 
                  onClick={handleTip}
                  disabled={tipping || !tipAmount}
                  style={{
                    background: (!tipAmount || tipping) ? 'rgba(255,255,255,0.05)' : 'var(--accent-gradient, linear-gradient(135deg, #ffb703, #ffc130))',
                    color: (!tipAmount || tipping) ? 'var(--text-muted)' : '#000',
                    border: 'none', padding: '0 20px', borderRadius: 'var(--button-radius, 8px)',
                    fontWeight: '700', fontSize: '14px', cursor: (!tipAmount || tipping) ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
                  }}
                >
                  {tipping ? <FaSpinner className="fa-spin" /> : 'Tip'}
                </button>
              </div>
              <AnimatePresence>
                {tipStatus && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    style={{ marginTop: '12px', fontSize: '13px', color: tipStatus.type === 'success' ? 'var(--success, #4cd964)' : 'var(--danger, #ff3b30)', textAlign: 'center' }}>
                    {tipStatus.message}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Admin Actions */}
            {(user?.isAdmin || user?.roles?.includes('admin')) && (
              <div style={{
                marginTop: '24px', background: 'var(--primary, #0e0e1a)', border: '1px solid var(--border, #1c1c32)',
                borderRadius: 'var(--border-radius, 12px)', padding: '20px'
              }}>
                <div style={{ color: 'var(--danger, #ff3b30)', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <FaShieldAlt /> Admin Quick Actions
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <button 
                    onClick={async () => {
                      const mode = stats.isChatMuted ? 'unmute' : 'mute';
                      if(!window.confirm(`${mode.toUpperCase()} this user?`)) return;
                      const token = localStorage.getItem('token');
                      await fetch(`${BACKEND_URL}/api/admin/user/${stats.userId}/${mode}`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: mode === 'mute' ? JSON.stringify({ duration: 60, reason: 'Muted via quick-access' }) : undefined
                      });
                      fetchStats(username);
                    }}
                    style={{
                      background: 'var(--warning, #ffcc00)', color: '#000', border: 'none', borderRadius: 'var(--button-radius, 8px)',
                      padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    {stats.isChatMuted ? 'Unmute User' : 'Mute (1h)'}
                  </button>

                  <button 
                    onClick={async () => {
                      const mode = stats.isBanned ? 'unban' : 'ban';
                      if(!window.confirm(`${mode.toUpperCase()} this user?`)) return;
                      const token = localStorage.getItem('token');
                      await fetch(`${BACKEND_URL}/api/admin/user/${stats.userId}/${mode}`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: mode === 'ban' ? JSON.stringify({ reason: 'Banned via quick-access' }) : undefined
                      });
                      fetchStats(username);
                    }}
                    style={{
                      background: 'var(--danger, #ff3b30)', color: '#fff', border: 'none', borderRadius: 'var(--button-radius, 8px)',
                      padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    {stats.isBanned ? 'Unban User' : 'Ban User'}
                  </button>

                  <button 
                    onClick={async () => {
                      const mode = window.confirm('Click OK to ADD balance, Cancel to REMOVE balance') ? 'add' : 'remove';
                      const amount = window.prompt(`Enter amount to ${mode.toUpperCase()}:`);
                      if(!amount) return;
                      const token = localStorage.getItem('token');
                      await fetch(`${BACKEND_URL}/api/admin/user/${stats.userId}/${mode}-balance`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ amount: parseFloat(amount) })
                      });
                      fetchStats(username);
                    }}
                    style={{
                      background: 'var(--success, #4cd964)', color: '#000', border: 'none', borderRadius: 'var(--button-radius, 8px)',
                      padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    Manage Bal
                  </button>

                  <button 
                    onClick={async () => {
                      if(!window.confirm('Reset this users free coins cooldown?')) return;
                      const token = localStorage.getItem('token');
                      await fetch(`${BACKEND_URL}/api/admin/user/${stats.userId}/reset-free-coins`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                      });
                      alert('Reset successful');
                    }}
                    style={{
                      background: 'var(--info, #5ac8fa)', color: '#000', border: 'none', borderRadius: 'var(--button-radius, 8px)',
                      padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    Reset CD
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0', minHeight: '200px' }}>
            <span style={{ color: 'var(--danger)', fontWeight: '600' }}>Failed to load profile.</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};
export default UserStatsModal;
